import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trash2, Key, Calendar, Clock } from 'lucide-react';
import { api } from '../services/api';

interface WebAuthnCredential {
  id: string;
  deviceName: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export default function WebAuthnManagement() {
  const [credentials, setCredentials] = useState<WebAuthnCredential[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.auth.webauthnCredentialsList();
      setCredentials(data || []);
    } catch (err: any) {
      setError(err.message || '載入金鑰失敗');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (credentialId: string) => {
    if (!confirm('確定要移除此金鑰嗎？')) return;

    try {
      setDeletingId(credentialId);
      await api.auth.webauthnCredentialsDelete(credentialId);
      setCredentials(credentials.filter(c => c.id !== credentialId));
    } catch (err: any) {
      setError(err.message || '移除金鑰失敗');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '從未使用';
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Key size={24} className="text-primary" />
          <div>
            <h2 className="text-2xl font-bold tracking-tighter">登入金鑰管理</h2>
            <p className="text-sm text-muted-foreground">管理您已註冊的 WebAuthn 金鑰</p>
          </div>
        </div>
      </div>

      {error && (
        <Card className="bg-destructive/10 border-destructive/20 p-4">
          <p className="text-sm font-medium text-destructive">{error}</p>
        </Card>
      )}

      {isLoading && (
        <Card className="p-8">
          <div className="flex justify-center">
            <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin"></div>
          </div>
        </Card>
      )}

      {!isLoading && credentials.length === 0 && (
        <Card className="p-8 text-center">
          <Key size={48} className="mx-auto text-muted-foreground mb-4 opacity-50" />
          <p className="text-muted-foreground font-medium">尚未註冊任何金鑰</p>
          <p className="text-xs text-muted-foreground mt-1">註冊 WebAuthn 金鑰以便快速登入</p>
        </Card>
      )}

      {!isLoading && credentials.length > 0 && (
        <div className="space-y-3">
          {credentials.map((cred) => (
            <Card
              key={cred.id}
              className="p-4 border-border/50 hover:border-border/80 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <Key size={18} className="text-primary" />
                    <h3 className="font-semibold text-foreground">{cred.deviceName}</h3>
                  </div>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Calendar size={14} />
                      <span>建立時間：{formatDate(cred.createdAt)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock size={14} />
                      <span>最後使用：{formatDate(cred.lastUsedAt)}</span>
                    </div>
                  </div>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={deletingId === cred.id}
                  onClick={() => handleDelete(cred.id)}
                  className="ml-4 gap-2"
                >
                  {deletingId === cred.id ? (
                    <>
                      <div className="w-3 h-3 border-2 border-current/30 border-t-current rounded-full animate-spin"></div>
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      移除
                    </>
                  )}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
